import { useState, useMemo, useEffect } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Building, Database, Eye, Download, FileSpreadsheet, FileText, CheckCircle, XCircle, PieChartIcon, CheckCircle2, Image, Users, Trash2, Pencil, Search, BookOpen, Save, Settings, ShieldAlert
} from "lucide-react";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { usePartiesStore, PartyProfile, savePartyProfile, getStrictDelegateMode, setStrictDelegateMode, adminSendProfileMessage, adminBlockProfileModifications, adminAuthorizeProfileModification } from "@/data/partiesStore";
import { useSapStore, SAPCandidate } from "@/data/sapStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { generateExport, getExportWatermarkEnabled, setExportWatermarkEnabled } from "@/utils/exportUtils";
import { getPostersConfig, savePostersConfig, PostersConfig } from "@/data/postersStore";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import AdminSapFormDialog from "@/components/admin/AdminSapFormDialog";
import AdminPosterFormDialog from "@/components/admin/AdminPosterFormDialog";
import AdminPartyProfileEditor from "@/components/admin/AdminPartyProfileEditor";
import { PartyPoster } from "@/data/partiesStore";
import { getPartyMetrics } from "@/data/partyMetricsStore";
import { useGameRulesStore } from "@/data/gameRulesStore";
import { fetchPublicCandidates } from "@/data/candidatesStore";
import { useSearchParams } from "react-router-dom";

export default function AdminPartisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const engagements: any[] = []; // En attente de la nouvelle API d'engagements réels
  
  const { rules, setRules } = useGameRulesStore();
  const [editingRules, setEditingRules] = useState({ ...rules });
  const [isSavingRules, setIsSavingRules] = useState(false);
  
  const handleSaveRules = () => {
    setIsSavingRules(true);
    setRules(editingRules);
    toast({ title: "Règles sauvegardées", description: "Les règles des jeux ont été mises à jour avec succès et sont visibles par les partis." });
    setTimeout(() => setIsSavingRules(false), 500);
  };

  const [watermarkEnabled, setWatermarkEnabled] = useState(getExportWatermarkEnabled());
  const handleToggleWatermark = (val: boolean) => {
     setExportWatermarkEnabled(val);
     setWatermarkEnabled(val);
     toast({ title: "Paramètres mis à jour", description: val ? "Filigrane Kayn'in activé pour les exports." : "Filigrane désactivé." });
  };

  const [strictModeEnabled, setStrictModeEnabled] = useState(getStrictDelegateMode());
  const handleToggleStrictMode = (val: boolean) => {
     setStrictDelegateMode(val);
     setStrictModeEnabled(val);
     toast({ title: "Sécurité Accès Délégués", description: val ? "Code strict obligatoire activé." : "Mode Démo activé (tout code accepté)." });
  };

  const [allCandidates, setAllCandidates] = useState<any[]>([]);
  const [candidateFilterParty, setCandidateFilterParty] = useState<string>("Tous");
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);

  useEffect(() => {
    fetchPublicCandidates().then(data => {
      const candidates = data.map(c => ({
        ...c,
        partyBase: c.partyAcronym,
        prenomCandidat: c.firstName,
        nomCandidat: c.lastName,
        prenomCandidatAr: c.firstNameAr,
        nomCandidatAr: c.lastNameAr,
      }));
      setAllCandidates(candidates);
      setIsLoadingCandidates(false);
    });
  }, []);

  // --- STATS COMPUTATIONS (Aperçu) ---
  const partyStats = useMemo(() => {
    return allPartiesIncludingNonParliamentary.map(p => {
      const pEngagements = engagements.filter(e => e.party === p.acronym);
      return {
        acronym: p.acronym, name: p.acronym, color: p.color, symbol: p.symbol,
        total: pEngagements.length,
        pending: pEngagements.filter(e => e.status === "pending").length,
        transferred: pEngagements.filter(e => e.status === "transferred").length,
        transferredList: pEngagements.filter(e => e.status === "transferred"),
        engagementsList: pEngagements
      };
    }).filter(s => s.total > 0 || ["RNI", "PAM", "PI"].includes(s.acronym)).sort((a, b) => b.total - a.total);
  }, [engagements]);

  // --- EXPORT FUNCTION ---
  const exportData = (data: any[], filename: string, format: 'excel' | 'word' | 'pdf') => {
    if (!data.length) {
      toast({ title: "Vide", description: "Aucune donnée à exporter.", variant: "destructive" });
      return;
    }
    toast({ title: "Export", description: `Génération export ${format.toUpperCase()} en cours...` });
    
    generateExport(data, filename, filename, format);
  };

  // --- PARTY PROFILES TAB LOGIC ---
  const { profiles, fetchProfiles, adminValidateProfile, adminSendProfileMessage: sendProfileMsg, adminBlockProfileModifications: blockModifs, adminAuthorizeProfileModification: authModif, saveProfile } = usePartiesStore();
  const [filterProfiles, setFilterProfiles] = useState("_all_");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [messageInputs, setMessageInputs] = useState<Record<string, string>>({});
  
  // --- ADMIN EDIT PROFILE ---
  const [editingPartyAcronym, setEditingPartyAcronym] = useState<string>("");
  const [isSavingAdminProfile, setIsSavingAdminProfile] = useState(false);
  const selectedProfileToEdit = profiles.find(p => p.acronym === editingPartyAcronym) || 
    allPartiesIncludingNonParliamentary.find(p => p.acronym === editingPartyAcronym) as any || null;

  // --- POSTERS CONFIG LOGIC ---
  const [postersConfig, setPostersConfig] = useState<PostersConfig>(getPostersConfig());
  const handleConfigChange = (key: keyof PostersConfig, value: boolean) => {
    const nextConfig = { ...postersConfig, [key]: value };
    setPostersConfig(nextConfig);
    savePostersConfig(nextConfig);
    toast({ title: "Configuration Affiches", description: "Les paramètres des affiches ont été mis à jour."});
  };

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  // --- SAP LOGIC ---
  const sapCandidates = useSapStore(state => state.candidates);
  const deleteSAPCandidate = useSapStore(state => state.deleteCandidateInternal);
  const saveSAPCandidate = useSapStore(state => state.addCandidateInternal);
  const [isSapDialogOpen, setIsSapDialogOpen] = useState(false);
  const [editingSap, setEditingSap] = useState<SAPCandidate | null>(null);

  // --- POSTERS TAB ---
  const [searchPartyQuery, setSearchPartyQuery] = useState("");
  const [isPosterDialogOpen, setIsPosterDialogOpen] = useState(false);
  const [editingPosterAcronym, setEditingPosterAcronym] = useState<string | null>(null);
  const [editingPosterData, setEditingPosterData] = useState<PartyPoster | null>(null);

  // useEffect removed for sapCandidates as Zustand handles it
  
  const handleDeleteSap = (id: string) => {
    if (confirm("Voulez-vous vraiment supprimer ce candidat SAP ?")) {
      deleteSAPCandidate(id);
      toast({ title: "Supprimé", description: "Le candidat SAP a été supprimé." });
    }
  };

  const handleSaveSap = (c: SAPCandidate) => {
    saveSAPCandidate(c);
    toast({ title: "Succès", description: "Candidat SAP enregistré avec succès." });
  };

  const handleUpdateFullPoster = (acronym: string, posterData: any | null, customData?: { acronym: string, fullNameFr: string, fullNameAr: string, color: string }) => {
    let profile = profiles.find(p => p.acronym === acronym);
    if (!profile) {
      // Create a basic profile if it doesn't exist
      profile = {
        acronym, 
        fullNameFr: customData?.fullNameFr || acronym, 
        fullNameAr: customData?.fullNameAr || acronym, 
        focusFr: "", focusAr: "",
        logoUrl: "", website: "", socials: {}, status: "valide",
        isCustom: !!customData,
        color: customData?.color
      };
    }
    const updated = { 
      ...profile, 
      poster: posterData || undefined, 
      fullPosterUrl: posterData?.url || "" 
    };
    saveProfile(updated);
    toast({ 
      title: posterData ? "Affiche ajoutée/mise à jour" : "Affiche supprimée", 
      description: `L'affiche de ${acronym} a été modifiée.` 
    });
  };

  const filteredProfiles = profiles.filter(p => filterProfiles === "_all_" || p.status === filterProfiles);
  
  async function validateProfile(acronym: string, status: "valide" | "rejete") {
    setSavingKey(acronym);
    const msg = messageInputs[acronym];
    await adminValidateProfile(acronym, status, user?.name || "Admin Inconnu", msg);
    setMessageInputs(prev => ({...prev, [acronym]: ""}));
    setSavingKey(null);
  }

  async function sendRemarque(acronym: string) {
    const msg = messageInputs[acronym];
    if (!msg) return toast({ title: "Erreur", description: "Veuillez entrer un message.", variant: "destructive" });
    setSavingKey(acronym);
    await sendProfileMsg(acronym, msg);
    setMessageInputs(prev => ({...prev, [acronym]: ""}));
    setSavingKey(null); 
    toast({ title: "Envoyé", description: "Remarque envoyée au parti."}); 
  }

  const handleAdminSaveProfile = async (profileData: PartyProfile) => {
    setIsSavingAdminProfile(true);
    // On s'assure que le profil garde un statut "valide" si l'admin l'a édité manuellement
    const updatedProfile = { 
      ...profileData, 
      status: profileData.status === "non_defini" || profileData.status === "en_attente" ? "valide" : profileData.status 
    } as PartyProfile;
    
    const success = await saveProfile(updatedProfile);
    setIsSavingAdminProfile(false);
    
    if (success) {
      await fetchProfiles();
      toast({ title: "Profil mis à jour", description: `Les informations du parti ${profileData.acronym} ont été enregistrées avec succès.` });
    } else {
      // Fetch the old DB values back to the UI since the save failed
      await fetchProfiles(); 
    }
  };

  function exportProfilePDF(p: PartyProfile) {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    doc.setFontSize(16); doc.text(`Profil Parti : ${p.acronym}`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Validé par : ${p.validatedBy || "Non validé"} le ${p.validatedAt ? new Date(p.validatedAt).toLocaleString("fr-FR") : "-"}`, 14, 28);
    autoTable(doc, {
      startY: 35,
      head: [["Propriété", "Valeur"]],
      body: [
        ["Nom Fr", p.fullNameFr],
        ["Nom Ar", p.fullNameAr],
        ["Axe Majeur (FR)", p.focusFr],
        ["Axe Majeur (AR)", p.focusAr],
        ["Site Web", p.website],
        ["Facebook", p.socials.facebook || "-"],
        ["X", p.socials.twitter || "-"],
        ["Instagram", p.socials.instagram || "-"],
        ["Statut", p.status],
      ],
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 40 }, 1: { cellWidth: 140 } },
    });
    doc.save(`profil_${p.acronym}_${new Date().getTime()}.pdf`);
  }

  function exportProfileWord(p: PartyProfile) {
    const htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>Profil ${p.acronym}</title></head>
      <body style="font-family: Arial, sans-serif;">
        <h1 style="color: #1e3a8a;">Fiche du Parti : ${p.acronym}</h1>
        <p><strong>Date de soumission :</strong> ${p.submittedAt ? new Date(p.submittedAt).toLocaleString("fr-FR") : "-"}</p>
        <p><strong>Statut :</strong> ${p.status.toUpperCase()}</p>
        <div style="background-color: #f3f4f6; padding: 15px; margin-bottom: 20px;">
          <p><strong>Informations validées par :</strong> ${p.validatedBy || "Action requise"}</p>
          <p><strong>Date de validation :</strong> ${p.validatedAt ? new Date(p.validatedAt).toLocaleString("fr-FR") : "-"}</p>
        </div>
        <table border="1" style="border-collapse: collapse; width: 100%;">
          <tr><th style="padding: 8px; text-align: left; background: #e5e7eb; width: 30%;">Nom Complet (FR)</th><td style="padding: 8px;">${p.fullNameFr}</td></tr>
          <tr><th style="padding: 8px; text-align: left; background: #e5e7eb;">Nom Complet (AR)</th><td style="padding: 8px;" dir="rtl">${p.fullNameAr}</td></tr>
          <tr><th style="padding: 8px; text-align: left; background: #e5e7eb;">Axe Majeur (FR)</th><td style="padding: 8px;">${p.focusFr}</td></tr>
          <tr><th style="padding: 8px; text-align: left; background: #e5e7eb;">Axe Majeur (AR)</th><td style="padding: 8px;" dir="rtl">${p.focusAr}</td></tr>
          <tr><th style="padding: 8px; text-align: left; background: #e5e7eb;">Site Web</th><td style="padding: 8px;">${p.website}</td></tr>
          <tr><th style="padding: 8px; text-align: left; background: #e5e7eb;">Facebook</th><td style="padding: 8px;">${p.socials.facebook || "-"}</td></tr>
          <tr><th style="padding: 8px; text-align: left; background: #e5e7eb;">X</th><td style="padding: 8px;">${p.socials.twitter || "-"}</td></tr>
        </table>
      </body></html>
    `;
    const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `profil_${p.acronym}_${new Date().getTime()}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h1 className="text-xl md:text-3xl font-display font-bold text-foreground">Gestion des Partis Politiques</h1>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-2">
          <p className="text-muted-foreground font-body text-sm">Vue globale, statistiques dédiées et modération des profils publics de chaque formation.</p>
        </div>
      </div>

      <Tabs value={searchParams.get("tab") || "inscrits"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="mb-6 h-auto bg-muted/50 p-1 flex flex-wrap gap-1 max-w-2xl rounded-xl overflow-x-auto">
          <TabsTrigger value="inscrits" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <Database className="w-4 h-4" /> Partis Inscrits
          </TabsTrigger>
          <TabsTrigger value="candidats" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <Building className="w-4 h-4" /> Têtes de Liste
          </TabsTrigger>
          <TabsTrigger value="affiches" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <PieChartIcon className="w-4 h-4" /> Affiches en ligne
          </TabsTrigger>
          <TabsTrigger value="affiches-completes" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <Image className="w-4 h-4" /> Affiches Complètes
          </TabsTrigger>
          <TabsTrigger value="sap" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> Indépendants (SAP)
          </TabsTrigger>
          <TabsTrigger value="edition" className="rounded-lg flex-1 font-semibold flex items-center gap-2 bg-blue-100 text-blue-800 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Pencil className="w-4 h-4" /> Édition Profils
          </TabsTrigger>
          <TabsTrigger value="regles" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Règles des jeux
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inscrits" className="mt-0">
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-display">Aperçu par parti politique</CardTitle>
              <CardDescription>Vue d'ensemble de l'activité liée à chaque parti (inscriptions et transferts des citoyens).</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {partyStats.map((stat) => (
                  <div key={stat.acronym} className="p-5 rounded-xl border border-border/50 bg-muted/10 flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: stat.color }} />
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-background shadow-sm border border-border flex items-center justify-center text-xl" style={{ color: stat.color }}>{stat.symbol || '🇲🇦'}</div>
                        <h4 className="font-display font-bold text-lg">{stat.acronym}</h4>
                      </div>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-primary hover:bg-primary/10" title="Statistiques du Parti">
                            <PieChartIcon className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl rounded-2xl">
                          <DialogHeader>
                            <DialogTitle>Statistiques & Export : {stat.acronym}</DialogTitle>
                            <DialogDescription>Aperçu détaillé des performances et engagements de {stat.acronym}.</DialogDescription>
                          </DialogHeader>
                          
                           {(() => {
                             const factor = stat.acronym.charCodeAt(0) + (stat.acronym.charCodeAt(1) || 0) || 150;
                             const allMetrics = getPartyMetrics();
                             const realLikes = allMetrics[stat.acronym]?.like || 0;
                             const metrics = {
                               visitesAffiche: factor * 432,
                               likesAffiche: Math.floor(factor * 432 * 0.12) + realLikes, // Combine real + mocked for demo
                               consultationsProgramme: factor * 125,
                               presentationParti: factor * 210,
                               clicsSiteWeb: factor * 85,
                               clicsReseaux: factor * 310,
                               eventsPublies: Math.floor(factor / 10),
                               interactions: factor * 45,
                               favorisPresents: realLikes
                             };
                             return (
                               <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                  <div className="bg-muted p-3 rounded-xl text-center border border-border/50">
                                    <p className="text-[10px] text-muted-foreground uppercase font-bold">Vues Affiche</p>
                                    <p className="text-xl font-display font-black text-foreground">{metrics.visitesAffiche.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-pink-50 p-3 rounded-xl text-center border border-pink-100 relative">
                                    <p className="text-[10px] text-pink-600 uppercase font-bold">Likes / Favoris</p>
                                    <p className="text-xl font-display font-black text-pink-700">{metrics.likesAffiche.toLocaleString()}</p>
                                    {metrics.favorisPresents > 0 && <span className="absolute top-1 right-1 text-[8px] bg-pink-600 text-white px-1 py-0.5 rounded-sm">+{metrics.favorisPresents} réels</span>}
                                  </div>
                                  <div className="bg-emerald-50 p-3 rounded-xl text-center border border-emerald-100">
                                    <p className="text-[10px] text-emerald-600 uppercase font-bold">Vues Programme</p>
                                    <p className="text-xl font-display font-black text-emerald-700">{metrics.consultationsProgramme.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-violet-50 p-3 rounded-xl text-center border border-violet-100">
                                    <p className="text-[10px] text-violet-600 uppercase font-bold">Vues Présent.</p>
                                    <p className="text-xl font-display font-black text-violet-700">{metrics.presentationParti.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-amber-50 p-3 rounded-xl text-center border border-amber-100">
                                    <p className="text-[10px] text-amber-600 uppercase font-bold">Clics Site Web</p>
                                    <p className="text-xl font-display font-black text-amber-700">{metrics.clicsSiteWeb.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-indigo-50 p-3 rounded-xl text-center border border-indigo-100">
                                    <p className="text-[10px] text-indigo-600 uppercase font-bold">Clics Réseaux</p>
                                    <p className="text-xl font-display font-black text-indigo-700">{metrics.clicsReseaux.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-orange-50 p-3 rounded-xl text-center border border-orange-100">
                                    <p className="text-[10px] text-orange-600 uppercase font-bold">Événements</p>
                                    <p className="text-xl font-display font-black text-orange-700">{metrics.eventsPublies.toLocaleString()}</p>
                                  </div>
                                  <div className="bg-cyan-50 p-3 rounded-xl text-center border border-cyan-100">
                                    <p className="text-[10px] text-cyan-600 uppercase font-bold">Interactions</p>
                                    <p className="text-xl font-display font-black text-cyan-700">{metrics.interactions.toLocaleString()}</p>
                                  </div>
                                </div>
                                <h4 className="text-sm font-bold border-b pb-2 mb-3 mt-4">Inscriptions & Engagements Citoyens</h4>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                  <div className="bg-muted p-4 rounded-xl text-center">
                                    <p className="text-xs text-muted-foreground uppercase">Total Inscrits</p>
                                    <p className="text-3xl font-display font-bold text-foreground">{stat.total}</p>
                                  </div>
                                  <div className="bg-emerald-50 p-4 rounded-xl text-center">
                                    <p className="text-xs text-emerald-600 uppercase">Validées</p>
                                    <p className="text-3xl font-display font-bold text-emerald-700">{stat.transferred}</p>
                                  </div>
                                  <div className="bg-orange-50 p-4 rounded-xl text-center">
                                    <p className="text-xs text-orange-600 uppercase">En Attente</p>
                                    <p className="text-3xl font-display font-bold text-orange-700">{stat.pending}</p>
                                  </div>
                                </div>
                               </>
                             );
                          })()}
                          
                          <div className="flex justify-end gap-3 mt-4">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="gap-2">
                                  <FileSpreadsheet className="w-4 h-4" /> Exporter TOUS
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 z-10">
                                <DropdownMenuItem onClick={() => exportData(stat.engagementsList, `Engagements_${stat.acronym}`, 'excel')} className="cursor-pointer">
                                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.csv)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportData(stat.engagementsList, `Engagements_${stat.acronym}`, 'word')} className="cursor-pointer">
                                  <FileText className="w-4 h-4 mr-2" /> Word (.doc)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportData(stat.engagementsList, `Engagements_${stat.acronym}`, 'pdf')} className="cursor-pointer">
                                  <FileText className="w-4 h-4 mr-2" /> PDF (.pdf)
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                                  <Download className="w-4 h-4" /> Transférés
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 z-10">
                                <DropdownMenuItem onClick={() => exportData(stat.transferredList, `Transferes_${stat.acronym}`, 'excel')} className="cursor-pointer">
                                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.csv)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportData(stat.transferredList, `Transferes_${stat.acronym}`, 'word')} className="cursor-pointer">
                                  <FileText className="w-4 h-4 mr-2" /> Word (.doc)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => exportData(stat.transferredList, `Transferes_${stat.acronym}`, 'pdf')} className="cursor-pointer">
                                  <FileText className="w-4 h-4 mr-2" /> PDF (.pdf)
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </DialogContent>
                      </Dialog>

                    </div>
                    
                    <div className="mt-auto grid grid-cols-2 gap-3 pb-3 border-b border-border/50">
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">En attente</span>
                        <span className="font-bold text-orange-500">{stat.pending}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">Validées</span>
                        <span className="font-bold text-emerald-600">{stat.transferred}</span>
                      </div>
                    </div>

                    <div className="mt-3">
                      {stat.transferred > 0 ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="w-full h-8 text-xs font-semibold gap-1.5 border-emerald-500/30 text-emerald-600 hover:bg-emerald-50">
                              <Eye className="w-3 h-3" /> Demandes Transférées
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl rounded-3xl p-0 overflow-hidden shadow-2xl border-none">
                              <div className="h-2 w-full" style={{ backgroundColor: stat.color }} />
                              <DialogHeader className="px-6 py-4 border-b border-border">
                                <DialogTitle>Engagements Valides : {stat.acronym}</DialogTitle>
                                <DialogDescription>Cédés historiquement au parti.</DialogDescription>
                              </DialogHeader>
                              <div className="max-h-[50vh] overflow-y-auto px-6 py-4 bg-muted/10 space-y-3">
                                {stat.transferredList.map(tEng => (
                                  <div key={tEng.id} className="bg-background rounded-xl border border-border p-4 shadow-sm">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <h5 className="font-bold text-sm">{tEng.firstName} {tEng.lastName}</h5>
                                        <div className="text-xs text-muted-foreground">{tEng.email} • {tEng.phone}</div>
                                      </div>
                                      <Badge variant="outline" className="text-[10px]">{tEng.date}</Badge>
                                    </div>
                                  </div>
                                ))}
                              </div>
                          </DialogContent>
                        </Dialog>
                      ) : <div className="text-center text-xs text-muted-foreground italic h-8 flex items-center justify-center">Aucune transmise</div>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="candidats" className="mt-0">
          <Card className="border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-xl font-display">Candidats Accrédités (Têtes de liste)</CardTitle>
                <CardDescription>Consultez ou exportez les listes électorales soumises par les partis.</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
                <div className="flex items-center gap-2 font-semibold">
                  <span>Filtrer par parti :</span>
                  <select 
                    className="h-10 rounded-lg border border-input px-3"
                    value={candidateFilterParty}
                    onChange={(e) => setCandidateFilterParty(e.target.value)}
                  >
                    <option value="Tous">Tous les partis</option>
                    {allPartiesIncludingNonParliamentary.filter(p => allCandidates.some(c => c.partyBase === p.acronym)).map(p => (
                      <option key={p.acronym} value={p.acronym}>{p.acronym}</option>
                    ))}
                  </select>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="gap-2 shrink-0 h-10 px-5 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white">
                      <Download className="w-4 h-4" /> Exporter cette liste
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 z-10">
                    <DropdownMenuItem onClick={() => {
                       const current = candidateFilterParty === "Tous" ? allCandidates : allCandidates.filter(c => c.partyBase === candidateFilterParty);
                       const title = candidateFilterParty === "Tous" ? `Liste des candidats (tête de liste) de tous les partis aux élections législatives 2026` : `Liste des candidats (tête de liste) du parti ${candidateFilterParty} aux élections législatives 2026`;
                       const formated = current.map(c => ({ Prénom: c.prenomCandidat, Nom: c.nomCandidat, Région: c.region, Circonscription: c.province || 'Régionale', Parti: c.partyBase }));
                       generateExport(formated, `Candidats_${candidateFilterParty}`, title, 'excel');
                    }} className="cursor-pointer">
                      <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.csv)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                       const current = candidateFilterParty === "Tous" ? allCandidates : allCandidates.filter(c => c.partyBase === candidateFilterParty);
                       const title = candidateFilterParty === "Tous" ? `Liste des candidats (tête de liste) de tous les partis aux élections législatives 2026` : `Liste des candidats (tête de liste) du parti ${candidateFilterParty} aux élections législatives 2026`;
                       const formated = current.map(c => ({ Prénom: c.prenomCandidat, Nom: c.nomCandidat, Région: c.region, Circonscription: c.province || 'Régionale', Parti: c.partyBase }));
                       generateExport(formated, `Candidats_${candidateFilterParty}`, title, 'pdf');
                    }} className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" /> PDF (.pdf)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                       const current = candidateFilterParty === "Tous" ? allCandidates : allCandidates.filter(c => c.partyBase === candidateFilterParty);
                       const title = candidateFilterParty === "Tous" ? `Liste des candidats (tête de liste) de tous les partis aux élections législatives 2026` : `Liste des candidats (tête de liste) du parti ${candidateFilterParty} aux élections législatives 2026`;
                       const formated = current.map(c => ({ Prénom: c.prenomCandidat, Nom: c.nomCandidat, Région: c.region, Circonscription: c.province || 'Régionale', Parti: c.partyBase }));
                       generateExport(formated, `Candidats_${candidateFilterParty}`, title, 'word');
                    }} className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" /> Word (.doc)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="border border-border/50 rounded-xl overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/50 font-semibold border-b border-border/50">
                    <tr>
                      <th className="px-4 py-3">Parti</th>
                      <th className="px-4 py-3">Candidat</th>
                      <th className="px-4 py-3">Région & Circ.</th>
                      <th className="px-4 py-3">Sociopro / Genre</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingCandidates ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                          <div className="flex justify-center my-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      (candidateFilterParty === "Tous" ? allCandidates : allCandidates.filter(c => c.partyBase === candidateFilterParty)).map((c, i) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="px-4 py-3 font-semibold">{c.partyBase}</td>
                        <td className="px-4 py-3 font-medium">
                          {c.prenomCandidat} {c.nomCandidat}
                          {c.prenomCandidatAr && <span className="block text-xs text-muted-foreground">{c.prenomCandidatAr} {c.nomCandidatAr}</span>}
                        </td>
                        <td className="px-4 py-3">
                          {c.region}<br/>
                          <span className="text-xs text-muted-foreground">{c.listType === 'locale' ? `Locale: ${c.province}` : 'Régionale'}</span>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {c.genre} • {c.categorieSociopro?.split("(")[0] || "Non spécifié"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm">Aperçu</Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle>Profil de Candidature</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="flex gap-4">
                                  {c.photoUrl ? <img src={c.photoUrl} className="w-16 h-16 rounded-xl object-cover" /> : <div className="w-16 h-16 bg-muted rounded-xl flex items-center justify-center"><Building className="w-8 h-8 text-muted-foreground"/></div>}
                                  <div>
                                    <h4 className="font-bold text-lg">{c.prenomCandidat} {c.nomCandidat} - {c.partyBase}</h4>
                                    <p className="text-sm text-muted-foreground">{c.listType === "locale" ? "Circonscription Locale" : "Circonscription Régionale"} de {c.province || c.region}</p>
                                  </div>
                                </div>
                                <div className="bg-muted/50 p-4 rounded-xl text-sm space-y-2">
                                  <p><strong>Arabe :</strong> {c.prenomCandidatAr} {c.nomCandidatAr}</p>
                                  <p><strong>Date de Naissance :</strong> {c.dateNaissance}</p>
                                  <p><strong>Catégorie :</strong> {c.categorieSociopro}</p>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </td>
                      </tr>
                    )))}
                    {!isLoadingCandidates && allCandidates.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Aucune candidature accréditée pour le moment.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="affiches" className="mt-0">
          <Card className="border-border shadow-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-xl font-display">Affiches (Page d'accueil)</CardTitle>
              <CardDescription>
                Contrôlez l'affichage des informations des partis (sièges, liens, clics) sur la carte de présentation en page d'accueil.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center py-10 bg-muted/20 border border-border/50 border-dashed rounded-xl">
                <Settings className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">Configurations des affiches déplacées</h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                  Tous les interrupteurs pour afficher/masquer les liens, le nombre de sièges, ou activer le silence électoral ont été centralisés dans le <strong>Centre de Contrôle (⚙️)</strong> accessible en bas du menu principal, onglet "Partis".
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="affiches-completes" className="mt-0">
          <Card className="border-border shadow-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-display">Affiches Complètes des Partis</CardTitle>
                  <CardDescription>
                    Gérez les affiches de campagne détaillées pour chaque parti.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4 w-full md:w-auto">
                  <div className="relative w-full md:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      placeholder="Rechercher un parti..." 
                      className="pl-9"
                      value={searchPartyQuery}
                      onChange={(e) => setSearchPartyQuery(e.target.value)}
                    />
                  </div>
                  <Button className="bg-primary text-white shrink-0 gap-2" onClick={() => {
                    setEditingPosterAcronym(null);
                    setEditingPosterData(null);
                    setIsPosterDialogOpen(true);
                  }}>
                    <Image className="w-4 h-4" /> Ajouter une affiche
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              
              <AdminPosterFormDialog 
                open={isPosterDialogOpen}
                onOpenChange={setIsPosterDialogOpen}
                initialAcronym={editingPosterAcronym}
                initialPoster={editingPosterData}
                customParties={profiles.filter(p => p.isCustom).map(p => ({ acronym: p.acronym }))}
                onSave={(acronym, poster, customData) => handleUpdateFullPoster(acronym, poster, customData)}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...allPartiesIncludingNonParliamentary, ...profiles.filter(p => p.isCustom).map(p => ({ acronym: p.acronym, color: p.color || "#000", symbol: "" }))]
                  .filter(p => p.acronym.toLowerCase().includes(searchPartyQuery.toLowerCase()) || (profiles.find(pr => pr.acronym === p.acronym)?.fullNameFr || "").toLowerCase().includes(searchPartyQuery.toLowerCase()))
                  .map(party => {
                  const profile = profiles.find(p => p.acronym === party.acronym);
                  const poster = profile?.poster;
                  
                  return (
                    <div key={party.acronym} className="p-4 rounded-xl border border-border bg-card flex flex-col">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl text-white shadow-sm font-bold shrink-0" style={{ backgroundColor: party.color }}>
                          {party.acronym.substring(0, 3)}
                        </div>
                        <h4 className="font-bold text-lg line-clamp-1">{profile?.fullNameFr || party.acronym}</h4>
                        {poster?.isActive ? (
                          <Badge className="ml-auto bg-emerald-100 text-emerald-700">Publié</Badge>
                        ) : poster ? (
                          <Badge className="ml-auto bg-amber-100 text-amber-700">Masqué</Badge>
                        ) : null}
                      </div>
                      
                      {poster?.url ? (
                        <div className="mb-4 relative rounded-lg overflow-hidden border border-border group">
                          <img src={poster.url} alt={`Affiche ${party.acronym}`} className="w-full h-48 object-cover" />
                        </div>
                      ) : (
                        <div className="mb-4 h-48 rounded-lg border-2 border-dashed border-border/60 bg-muted/30 flex flex-col items-center justify-center text-muted-foreground">
                          <Image className="w-8 h-8 mb-2 opacity-50" />
                          <span className="text-sm">Aucune affiche</span>
                        </div>
                      )}
                      
                      <div className="mt-auto space-y-3 pt-3 border-t border-border/50">
                        {poster && (
                          <div className="text-sm">
                            <p className="font-semibold line-clamp-1">{poster.titleFr}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{poster.descriptionFr}</p>
                          </div>
                        )}
                        <div className="flex gap-2 w-full">
                          <Button 
                            variant={poster ? "outline" : "default"}
                            className="flex-1"
                            onClick={() => {
                              setEditingPosterAcronym(party.acronym);
                              setEditingPosterData(poster || null);
                              setIsPosterDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-2" /> {poster ? "Modifier" : "Ajouter"}
                          </Button>
                          {poster && (
                            <Button variant="ghost" className="text-red-500 px-3" onClick={() => {
                              if (confirm("Supprimer cette affiche ?")) {
                                handleUpdateFullPoster(party.acronym, null);
                              }
                            }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sap" className="mt-0">
          <Card className="border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b">
              <div>
                <CardTitle className="text-xl font-display">Candidats Indépendants (SAP)</CardTitle>
                <CardDescription>Gérez les candidatures "Sans Appartenance Politique".</CardDescription>
              </div>
              <Button className="bg-primary text-white gap-2" onClick={() => { setEditingSap(null); setIsSapDialogOpen(true); }}>
                <Users className="w-4 h-4" /> Ajouter un candidat
              </Button>
            </CardHeader>
            <CardContent className="pt-6">
              
              <AdminSapFormDialog 
                open={isSapDialogOpen} 
                onOpenChange={setIsSapDialogOpen} 
                candidate={editingSap} 
                onSave={handleSaveSap} 
              />
              
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/50 font-semibold border-b border-border/50">
                    <tr>
                      <th className="px-4 py-3">Symbole</th>
                      <th className="px-4 py-3">Candidat (FR/AR)</th>
                      <th className="px-4 py-3">Région & Province</th>
                      <th className="px-4 py-3">Sociopro</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sapCandidates.map(c => (
                      <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="px-4 py-3 text-2xl">{c.symbol}</td>
                        <td className="px-4 py-3 font-medium">
                          {c.firstName} {c.lastName}
                          <span className="block text-xs text-muted-foreground">{c.firstNameAr} {c.lastNameAr}</span>
                        </td>
                        <td className="px-4 py-3">
                          {c.region}
                          <span className="block text-xs text-muted-foreground">{c.province}</span>
                        </td>
                        <td className="px-4 py-3 text-xs">{c.sociopro} • {c.genre}</td>
                        <td className="px-4 py-3">
                          {c.status === "valide" && <Badge className="bg-emerald-100 text-emerald-700">Validé</Badge>}
                          {c.status === "en_attente" && <Badge className="bg-orange-100 text-orange-700">En attente</Badge>}
                          {c.status === "brouillon" && <Badge className="bg-muted text-muted-foreground">Brouillon</Badge>}
                          {c.justificatifUrl && (
                             <a href={c.justificatifUrl} target="_blank" rel="noreferrer" className="block text-[10px] text-blue-600 hover:underline mt-1">
                               Voir Justificatif
                             </a>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" className="h-8 w-8 text-primary" onClick={() => { setEditingSap(c); setIsSapDialogOpen(true); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 text-red-500" onClick={() => handleDeleteSap(c.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {sapCandidates.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Aucun candidat indépendant enregistré.</td></tr>
                    )}
                    {sapCandidates.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Aucun candidat SAP.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="edition" className="mt-0">
          <Card className="border-border shadow-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-display flex items-center gap-2">
                    <Pencil className="w-6 h-6 text-blue-600" />
                    Édition des Profils (Pré-lancement)
                  </CardTitle>
                  <CardDescription>
                    Remplissez ou modifiez manuellement toutes les informations détaillées d'un parti (réseaux sociaux, structures, principes).
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-muted-foreground whitespace-nowrap">Sélectionner un parti :</span>
                  <select 
                    className="h-10 rounded-lg border border-input px-3 bg-background min-w-[200px]"
                    value={editingPartyAcronym}
                    onChange={(e) => setEditingPartyAcronym(e.target.value)}
                  >
                    <option value="" disabled>-- Choisir un parti --</option>
                    {allPartiesIncludingNonParliamentary.map(p => (
                      <option key={p.acronym} value={p.acronym}>{p.acronym} - {p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 bg-muted/10">
              {editingPartyAcronym && selectedProfileToEdit ? (
                <AdminPartyProfileEditor 
                  initialProfile={selectedProfileToEdit} 
                  onSave={handleAdminSaveProfile}
                  isSaving={isSavingAdminProfile}
                />
              ) : (
                <div className="text-center py-16 border-2 border-dashed border-border/60 rounded-xl bg-background">
                  <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="font-bold text-lg">Aucun parti sélectionné</h3>
                  <p className="text-muted-foreground mt-1">Veuillez sélectionner un parti politique dans le menu déroulant ci-dessus pour commencer à éditer son profil.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="regles" className="mt-0">
          <Card className="border-border shadow-sm">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-xl font-display flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-primary" />
                Règles des 3 Jeux (Quiz, Swipe, Ring)
              </CardTitle>
              <CardDescription>
                Modifiez ici le texte d'acceptation des règles pour chaque jeu. Ces textes s'afficheront dans l'espace des partis pour validation.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-8">
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-amber-500" />
                    Le Quiz Politique
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">Règles que le parti doit accepter avant de soumettre ses réponses au quiz.</p>
                  <textarea 
                    value={editingRules.quiz} 
                    onChange={e => setEditingRules({...editingRules, quiz: e.target.value})} 
                    className="w-full h-32 rounded-lg border border-input p-3 text-sm focus:ring-2 focus:ring-primary/20" 
                  />
                </div>

                <div className="border-t border-border/50 pt-4">
                  <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-pink-500" />
                    Match ton Parti (Swipe)
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">Règles d'acceptation des correspondances ludiques.</p>
                  <textarea 
                    value={editingRules.swipe} 
                    onChange={e => setEditingRules({...editingRules, swipe: e.target.value})} 
                    className="w-full h-32 rounded-lg border border-input p-3 text-sm focus:ring-2 focus:ring-primary/20" 
                  />
                </div>

                <div className="border-t border-border/50 pt-4">
                  <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                    <Building className="w-5 h-5 text-red-500" />
                    Le Ring (Arène)
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">Charte de bonne conduite et de débat dans l'arène publique.</p>
                  <textarea 
                    value={editingRules.ring} 
                    onChange={e => setEditingRules({...editingRules, ring: e.target.value})} 
                    className="w-full h-32 rounded-lg border border-input p-3 text-sm focus:ring-2 focus:ring-primary/20" 
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveRules} disabled={isSavingRules} className="gap-2">
                  {isSavingRules ? <span className="animate-spin">⟳</span> : <Save className="w-4 h-4" />}
                  Sauvegarder les règles
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
